import { useState } from 'react';
import { Wrench, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../ui/dialog';
import { Checkbox } from '../../ui/checkbox';
import api from '../../../services/api';
import { toast } from 'sonner';

/**
 * Audits + repairs age_category drift for one tournament. Surfaces the
 * three classes of issue (case mismatch / roster drift / unknown values)
 * before letting the admin commit.
 */
const AgeCategoryRepairButton = ({ tournamentId, onRepaired }) => {
  const [open, setOpen] = useState(false);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [fixCase, setFixCase] = useState(true);
  const [fixDrift, setFixDrift] = useState(false);

  const runAudit = async () => {
    setLoading(true);
    setAudit(null);
    try {
      const { data } = await api.get(`/tournaments/${tournamentId}/age-category-audit`);
      setAudit(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run audit');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (v) => {
    setOpen(v);
    if (v) runAudit();
  };

  const handleApply = async () => {
    if (!fixCase && !fixDrift) {
      toast.info('Select at least one repair option to apply.');
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post(`/tournaments/${tournamentId}/age-category-repair`, {
        fix_case_mismatches: fixCase,
        fix_roster_drift: fixDrift,
      });
      const s = data?.summary || {};
      const parts = [];
      if (s.case_fixed) parts.push(`${s.case_fixed} case fixes`);
      if (s.roster_drift_registrations_updated) parts.push(`${s.roster_drift_registrations_updated} drifted registrations updated in place`);
      if (s.roster_drift_skipped_multi_registration) parts.push(`${s.roster_drift_skipped_multi_registration} skipped (multiple registrations — see list)`);
      toast.success(parts.length ? `Repaired: ${parts.join(', ')}.` : 'No changes made.');
      if (s.roster_drift_skipped_multi_registration > 0 && data?.skipped_multi_reg_players?.length) {
        console.warn('[age-repair] multi-registration players skipped:', data.skipped_multi_reg_players);
      }
      setOpen(false);
      onRepaired?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply repairs');
    } finally {
      setApplying(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="text-amber-700 border-amber-300 hover:bg-amber-50"
        onClick={() => handleOpen(true)}
        title="Audit + repair age_category drift across registrations and team rosters"
        data-testid="age-category-repair-btn"
      >
        <Wrench className="h-4 w-4 mr-1" />
        Repair Categories
      </Button>

      <Dialog open={open} onOpenChange={handleOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="age-category-repair-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Repair Age Categories
            </DialogTitle>
            <DialogDescription>
              Scans registrations and team rosters for age_category drift that breaks RSVPs, manage-roster filters, and eligibility checks.
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Auditing...
            </div>
          )}

          {audit && !loading && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">Canonical categories:</span>
                {audit.canonical_categories?.map((c) => (
                  <Badge key={c} variant="outline" className="uppercase">{c}</Badge>
                ))}
              </div>

              {audit.total_issues === 0 ? (
                <div className="flex items-center gap-2 p-4 bg-emerald-50 border border-emerald-200 rounded text-emerald-700">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="text-sm">No age category issues found.</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Case mismatches */}
                  <div className="border rounded p-3" data-testid="audit-case-mismatches">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">Case mismatches</span>
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200">{audit.case_mismatches.length}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">
                      Registrations with non-lowercase age_category (e.g. "U10" vs "u10"). Safe to fix — pure normalization.
                    </p>
                    {audit.case_mismatches.slice(0, 5).map((m) => (
                      <div key={m.registration_id} className="text-xs text-slate-600 py-0.5">
                        · {m.player_name || m.player_id}: <code className="bg-slate-100 px-1 rounded">{m.current}</code> → <code className="bg-emerald-100 px-1 rounded">{m.should_be}</code>
                      </div>
                    ))}
                    {audit.case_mismatches.length > 5 && (
                      <p className="text-xs text-slate-400 mt-1">+{audit.case_mismatches.length - 5} more</p>
                    )}
                  </div>

                  {/* Roster drift */}
                  <div className="border rounded p-3" data-testid="audit-roster-drift">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">Roster drift</span>
                      <Badge className="bg-rose-100 text-rose-800 border-rose-200">{audit.roster_drift.length}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">
                      Players placed on a team in a category their registration doesn't cover. This is what blocks RSVPs and makes the Manage Roster dialog miss players. Fix adds a <strong>new</strong> registration in the team's category — never deletes.
                    </p>
                    {audit.roster_drift.slice(0, 5).map((d, idx) => (
                      <div key={idx} className="text-xs text-slate-600 py-0.5">
                        · {d.player_name || d.player_id} on <strong>{d.team_name}</strong> ({d.team_age_category.toUpperCase()}) — registered for: {d.player_registration_categories.length ? d.player_registration_categories.join(', ').toUpperCase() : 'NONE'}
                      </div>
                    ))}
                    {audit.roster_drift.length > 5 && (
                      <p className="text-xs text-slate-400 mt-1">+{audit.roster_drift.length - 5} more</p>
                    )}
                  </div>

                  {/* Unknown categories */}
                  {audit.unknown_categories.length > 0 && (
                    <div className="border rounded p-3" data-testid="audit-unknown-categories">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">Unknown categories</span>
                        <Badge variant="outline">{audit.unknown_categories.length}</Badge>
                      </div>
                      <p className="text-xs text-slate-500 mb-2">
                        Registrations whose age_category isn't in the tournament's allowed list. Left alone — repair them manually so you can decide which canonical category they should map to.
                      </p>
                      {audit.unknown_categories.slice(0, 5).map((u) => (
                        <div key={u.registration_id} className="text-xs text-slate-600 py-0.5">
                          · {u.player_name || u.player_id}: <code className="bg-slate-100 px-1 rounded">{u.current || '(empty)'}</code>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {audit.total_issues > 0 && (
                <div className="border-t pt-4 space-y-2">
                  <p className="text-sm font-medium">Repair options</p>
                  <label className="flex items-start gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                    <Checkbox checked={fixCase} onCheckedChange={(v) => setFixCase(!!v)} data-testid="repair-fix-case" />
                    <div className="text-sm">
                      <div className="font-medium">Fix case mismatches ({audit.case_mismatches.length})</div>
                      <div className="text-xs text-slate-500">Lowercase every registration's age_category. Safe + idempotent.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                    <Checkbox checked={fixDrift} onCheckedChange={(v) => setFixDrift(!!v)} data-testid="repair-fix-drift" />
                    <div className="text-sm">
                      <div className="font-medium">Fix roster drift in place ({audit.roster_drift.length})</div>
                      <div className="text-xs text-slate-500">
                        <strong>Updates</strong> (does NOT create) the drifted player's existing registration so its age_category matches the team. No duplicate registrations, no payment changes. Players with multiple registrations in this tournament are skipped and listed in the server response so you can resolve them manually.
                      </div>
                    </div>
                  </label>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={applying}>Close</Button>
            {audit?.total_issues > 0 && (
              <Button
                onClick={handleApply}
                disabled={applying || (!fixCase && !fixDrift)}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="repair-apply-btn"
              >
                {applying ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applying...</>) : (<><Wrench className="h-4 w-4 mr-2" /> Apply Selected</>)}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AgeCategoryRepairButton;
